import { useEffect, useState } from "react";
import { Modal, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import API from "../../common/API";

interface FlagsState {
  id: string;
  identificador: string;
  pep: boolean;
  padep: boolean;
  desaparecido: boolean;
  fallecido: boolean;
  empleadopublico: boolean;
  empleadoprivado: boolean;
  rasadep?: boolean;
  pip?: boolean;
  pecop?: boolean;
}

interface FlagsModalProps {
  show: boolean;
  identificador: string | undefined;
  onClose: () => void;
}

const FlagsModal = ({ show, identificador, onClose }: FlagsModalProps) => {
  const { t } = useTranslation();
  const [flagsLoading, setFlagsLoading] = useState(false);
  const [flagsSaving, setFlagsSaving] = useState(false);
  const [flags, setFlags] = useState<FlagsState>({
    id: "",
    identificador: "",
    pep: false,
    padep: false,
    desaparecido: false,
    fallecido: false,
    empleadopublico: false,
    empleadoprivado: false,
    rasadep: false,
    pip: false,
    pecop: false,
  });

  const loadFlags = async () => {
    if (!identificador) {
      return;
    }
    try {
      setFlagsLoading(true);
      const response = await API.getAction(
        `bandera/ByIdentificador?identificador=${identificador}`
      );
      if (response.status === 200 && response.data?.status === "ok") {
        const data = response.data.response ?? {};
        setFlags({
          id: data.id ?? "",
          identificador: data.identificador ?? identificador,
          pep: data.pep ?? false,
          padep: data.padep ?? false,
          desaparecido: data.desaparecido ?? false,
          fallecido: data.fallecido ?? false,
          empleadopublico: data.empleadopublico ?? false,
          empleadoprivado: data.empleadoprivado ?? false,
          rasadep: data.rasadep ?? false,
          pip: data.pip ?? false,
          pecop: data.pecop ?? false,
        });
      }
    } catch (error) {
      console.error("Error fetching flags:", error);
    } finally {
      setFlagsLoading(false);
    }
  };

  const saveFlags = async () => {
    if (!identificador) {
      return;
    }
    try {
      setFlagsSaving(true);
      const payload = {
        id: flags.id ?? "",
        identificador,
        pep: flags.pep ?? false,
        padep: flags.padep ?? false,
        desaparecido: flags.desaparecido ?? false,
        fallecido: flags.fallecido ?? false,
        empleadopublico: flags.empleadopublico ?? false,
        empleadoprivado: flags.empleadoprivado ?? false,
        rasadep: flags.rasadep ?? false,
        pip: flags.pip ?? false,
        pecop: flags.pecop ?? false,
      };
      const response = await API.postAction("bandera/AddEdit", payload);
      if (response.status === 200 && response.data?.status === "ok") {
        onClose();
      }
    } catch (error) {
      console.error("Error saving flags:", error);
    } finally {
      setFlagsSaving(false);
    }
  };

  useEffect(() => {
    if (show) {
      loadFlags();
    }
  }, [show, identificador]);

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t("Banderas")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {flagsLoading ? (
          <div className="text-center my-3">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <Form>
            <Form.Check
              type="switch"
              id="flag-pep"
              label="PEP"
              checked={!!flags.pep}
              onChange={e => setFlags({ ...flags, pep: e.target.checked })}
            />
            <Form.Check
              type="switch"
              id="flag-padep"
              label="PADEP"
              checked={!!flags.padep}
              onChange={e => setFlags({ ...flags, padep: e.target.checked })}
            />
            <Form.Check
              type="switch"
              id="flag-desaparecido"
              label={t("Desaparecido")}
              checked={!!flags.desaparecido}
              onChange={e =>
                setFlags({ ...flags, desaparecido: e.target.checked })
              }
            />
            <Form.Check
              type="switch"
              id="flag-fallecido"
              label={t("Fallecido")}
              checked={!!flags.fallecido}
              onChange={e => setFlags({ ...flags, fallecido: e.target.checked })}
            />
            <Form.Check
              type="switch"
              id="flag-empleado-publico"
              label={t("Empleado público")}
              checked={!!flags.empleadopublico}
              onChange={e =>
                setFlags({ ...flags, empleadopublico: e.target.checked })
              }
            />
            <Form.Check
              type="switch"
              id="flag-empleado-privado"
              label={t("Empleado privado")}
              checked={!!flags.empleadoprivado}
              onChange={e =>
                setFlags({ ...flags, empleadoprivado: e.target.checked })
              }
            />
            <Form.Check
              type="switch"
              id="flag-rasadep"
              label="RASADEP"
              checked={!!flags.rasadep}
              onChange={e =>
                setFlags({ ...flags, rasadep: e.target.checked })
              }
            />
            <Form.Check
              type="switch"
              id="flag-pip"
              label="PIP"
              checked={!!flags.pip}
              onChange={e =>
                setFlags({ ...flags, pip: e.target.checked })
              }
            />
            <Form.Check
              type="switch"
              id="flag-pecop"
              label="PECOP"
              checked={!!flags.pecop}
              onChange={e =>
                setFlags({ ...flags, pecop: e.target.checked })
              }
            />
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary" onClick={onClose} type="button">
          {t("Cancelar")}
        </button>
        <button
          className="btn btn-primary"
          onClick={saveFlags}
          type="button"
          disabled={flagsSaving}
        >
          {flagsSaving ? t("Guardando") : t("Guardar")}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default FlagsModal;
